import React, { useState, useEffect, useRef } from 'react';
import { collection, query, where, orderBy, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase';
import { PortvilleRoomWithResidency } from '../../../types/portvilleRoom';
import { downloadStorageFile } from '../../../utils/storageDownload';
import { MoveOutCharges } from '../../../utils/portvilleMoveOutCharges';
import MoveOutChargesSummary from './MoveOutChargesSummary';
import { loadMoveOutInspectionReport } from '../../../services/MoveOutInspectionReportService';
import type { MoveOutInspectionReport } from '../../../types/moveOutInspection';

type LotteryDocumentType = 'identification' | 'employment' | 'other';
type LotteryDocuments = Partial<Record<LotteryDocumentType, string>>;

const getDocumentDisplayName = (docType: LotteryDocumentType) => {
  switch (docType) {
    case 'identification':
      return '신분증 사본';
    case 'employment':
      return '재직증명서/사업자등록증';
    case 'other':
      return '기타 서류';
  }
};

const getOriginalFileNameFromStoragePath = (storagePath: string) => {
  const fileName = storagePath.split('/').pop() || storagePath;
  const extracted = fileName.split('_').slice(3).join('_');
  return extracted || fileName;
};

interface ContractHistory {
  id: string;
  contractorInfo: {
    name: string;
    phoneNumber: string;
    organization: string;
  };
  contractorUid?: string;
  occupancyPeriod: {
    startDate: Date;
    endDate: Date;
  };
  contractSignedAt: Date;
  signatureUrl?: string;
  moveOutInspection?: {
    id?: string;
    inspectorName?: string;
    charges?: MoveOutCharges;
    signedAt?: Date;
    signatureUrl?: string;
    notes?: string;
    meterReadings?: {
      electricity?: string;
      water?: string;
      gas?: string;
    };
    items?: Array<{
      id: string;
      category?: string;
      name?: string;
      checked?: boolean;
      notes?: string;
    }>;
  };
  inspectionOccupancyPeriod?: { startDate: Date; endDate: Date };
  createdAt: Date;
}

interface LotteryHistory {
  id: string;
  uid: string;
  userName?: string;
  userPhone?: string;
  userOrganization?: string;
  roomNumber: number;
  status: 'drawn' | 'contracted';
  drawnAt: Date;
  contractedAt?: Date;
  documents?: LotteryDocuments;
  documentsUpdatedAt?: Date;
}

interface RoomHistoryModalProps {
  room: PortvilleRoomWithResidency;
  isOpen: boolean;
  onClose: () => void;
}

const RoomHistoryModal: React.FC<RoomHistoryModalProps> = ({ room, isOpen, onClose }) => {
  const [contractHistory, setContractHistory] = useState<ContractHistory[]>([]);
  const [lotteryHistory, setLotteryHistory] = useState<LotteryHistory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedContractId, setExpandedContractId] = useState<string | null>(null);
  const [downloadingPath, setDownloadingPath] = useState<string | null>(null);
  const [generatingInspectionId, setGeneratingInspectionId] = useState<string | null>(null);
  const [pdfError, setPdfError] = useState<{ id: string; message: string } | null>(null);
  const pdfBusy = useRef(false);

  useEffect(() => {
    if (isOpen && room) {
      loadRoomHistory();
    }
  }, [isOpen, room]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadRoomHistory = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // 새로운 입주 기록 컬렉션에서 조회 (모든 상태 포함)
      const residenciesQuery = query(
        collection(db, 'portville_residencies'),
        where('roomId', '==', room.id),
        orderBy('createdAt', 'desc')
      );

      const residenciesSnapshot = await getDocs(residenciesQuery);
      const contracts: ContractHistory[] = [];

      residenciesSnapshot.forEach((doc) => {
        const data = doc.data();
        // 계약이 완료된 기록만 히스토리로 표시
        if (data.contractSignedAt) {
          contracts.push({
            id: doc.id,
            contractorInfo: {
              name: data.residentInfo?.name || '',
              phoneNumber: data.residentInfo?.phoneNumber || '',
              organization: data.residentInfo?.organization || ''
            },
            contractorUid: data.contractorUid, // 새로운 구조에서는 없을 수 있음
            occupancyPeriod: data.occupancyPeriod ? {
              startDate: data.occupancyPeriod.startDate.toDate(),
              endDate: data.occupancyPeriod.endDate.toDate()
            } : {
              startDate: new Date(),
              endDate: new Date()
            },
            contractSignedAt: data.contractSignedAt.toDate(),
            signatureUrl: data.signatureUrl,
            inspectionOccupancyPeriod: data.occupancyPeriod ? {
              startDate: data.occupancyPeriod.startDate.toDate(),
              endDate: data.occupancyPeriod.endDate.toDate()
            } : undefined,
            moveOutInspection: data.moveOutInspectionSignedAt ? {
              id: data.moveOutInspectionId,
              inspectorName: data.moveOutInspectionInspectorName?.trim() || undefined,
              signedAt: data.moveOutInspectionSignedAt?.toDate?.() || undefined,
              signatureUrl: data.moveOutInspectionSignatureUrl,
              notes: data.moveOutInspectionNotes,
              charges: data.moveOutInspectionCharges,
              meterReadings: data.moveOutMeterReadings || undefined,
              items: data.moveOutInspectionItems || undefined
            } : undefined,
            createdAt: data.createdAt.toDate()
          });
        }
      });

      // 기존 점검의 점검자명은 별도 점검 기록에만 저장되어 있다.
      // 입주기록과 점검 시각을 모두 비교해 다른 점검의 이름이 섞이지 않도록 한다.
      if (contracts.some(record => record.moveOutInspection && !record.moveOutInspection.inspectorName)) {
        const inspectionsSnapshot = await getDocs(query(
          collection(db, 'portville_inspections'),
          where('roomId', '==', room.id)
        ));
        const inspectorNames = new Map<string, string>();
        inspectionsSnapshot.forEach(snapshot => {
          const inspection = snapshot.data();
          const inspectedAt = inspection.inspectedAt?.toDate?.();
          if (inspection.inspectionType === 'move-out' && inspection.residencyId &&
              inspectedAt && typeof inspection.inspectorName === 'string' && inspection.inspectorName.trim()) {
            inspectorNames.set(`${inspection.residencyId}:${inspectedAt.getTime()}`, inspection.inspectorName.trim());
          }
        });
        contracts.forEach(record => {
          const inspection = record.moveOutInspection;
          if (inspection?.signedAt && !inspection.inspectorName) {
            inspection.inspectorName = inspectorNames.get(`${record.id}:${inspection.signedAt.getTime()}`);
          }
        });
      }

      // 추첨 기록 조회 (기존 방식 유지)
      const lotteryQuery = query(
        collection(db, 'portville_lottery_history'),
        where('roomNumber', '==', parseInt(room.roomNumber)),
        orderBy('drawnAt', 'desc')
      );

      const lotterySnapshot = await getDocs(lotteryQuery);
      const lotteries: LotteryHistory[] = [];

      // 각 추첨 기록에 대해 사용자 정보 조회
      for (const lotteryDoc of lotterySnapshot.docs) {
        const data = lotteryDoc.data();
        let userName = '';
        let userPhone = '';
        let userOrganization = '';

        // uid로 사용자 프로필 조회
        if (data.uid) {
          try {
            const userProfileDoc = await getDoc(doc(db, 'user_profiles', data.uid));
            if (userProfileDoc.exists()) {
              const userProfile = userProfileDoc.data();
              userName = userProfile.name || '';
              userPhone = userProfile.phoneNumber || '';
              userOrganization = userProfile.organization || '';
            }
          } catch (userError) {
            console.warn('사용자 프로필 조회 실패:', data.uid, userError);
          }
        }

        lotteries.push({
          id: lotteryDoc.id,
          uid: data.uid,
          userName,
          userPhone,
          userOrganization,
          roomNumber: data.roomNumber,
          status: data.status,
          drawnAt: data.drawnAt.toDate(),
          contractedAt: data.contractedAt ? data.contractedAt.toDate() : undefined,
          documents: (data.documents || {}) as LotteryDocuments,
          documentsUpdatedAt: data.documentsUpdatedAt ? data.documentsUpdatedAt.toDate?.() : undefined
        });
      }

      setContractHistory(contracts);
      setLotteryHistory(lotteries);
    } catch (err) {
      console.error('입주 히스토리 로드 실패:', err);
      setError('입주 히스토리를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const downloadInspection = async (record: ContractHistory) => {
    const inspection = record.moveOutInspection;
    if (pdfBusy.current || !inspection?.signedAt) return;
    pdfBusy.current = true;
    setGeneratingInspectionId(record.id);
    setPdfError(null);
    const fallback: MoveOutInspectionReport = {
      id: `residency-${record.id}`, roomId: room.id, roomNumber: room.roomNumber,
      residencyId: record.id, inspectedAt: inspection.signedAt,
      residentInfo: record.contractorInfo, occupancyPeriod: record.inspectionOccupancyPeriod,
      inspectorName: inspection.inspectorName, inspectionItems: inspection.items,
      inspectionNotes: inspection.notes, meterReadings: inspection.meterReadings,
      charges: inspection.charges, signatureUrl: inspection.signatureUrl
    };
    try {
      const report = await loadMoveOutInspectionReport(fallback, inspection.id);
      const { downloadMoveOutInspectionPdf } = await import('../../../utils/moveOutInspectionPdf');
      await downloadMoveOutInspectionPdf(report);
    } catch (error) {
      setPdfError({ id: record.id, message: error instanceof Error ? error.message : 'PDF 생성에 실패했습니다. 다시 시도해주세요.' });
    } finally {
      pdfBusy.current = false;
      setGeneratingInspectionId(null);
    }
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateTime = (date: Date) => {
    return date.toLocaleString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const downloadLotteryDocument = async (storagePath: string, filenameBase: string) => {
    try {
      setDownloadingPath(storagePath);
      await downloadStorageFile(storagePath, filenameBase);
    } catch (downloadError) {
      console.error('추첨 서류 다운로드 실패:', downloadError);
      setError('추첨 서류를 다운로드하지 못했습니다.');
    } finally {
      setDownloadingPath(null);
    }
  };



  if (!isOpen) return null;

  type MoveOutInspectionItems = NonNullable<ContractHistory['moveOutInspection']>['items'];
  const groupedMoveOutItems = (items?: MoveOutInspectionItems) => {
    if (!items || items.length === 0) return [];
    const byCategory = new Map<string, typeof items>();
    for (const item of items) {
      const category = item.category || '기타';
      const existing = byCategory.get(category) || [];
      existing.push(item);
      byCategory.set(category, existing);
    }
    return Array.from(byCategory.entries());
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* 헤더 */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">
            {room.roomNumber}호 입주 히스토리
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* 내용 */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-lg text-gray-600">로딩 중...</div>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <div className="text-red-800">{error}</div>
            </div>
                     ) : contractHistory.length === 0 && lotteryHistory.length === 0 ? (
             <div className="text-center py-8">
               <div className="text-gray-500">입주 히스토리가 없습니다.</div>
             </div>
          ) : (
            <div className="space-y-6">
              {/* 현재 입주자 정보 */}
              {room.currentResident && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-blue-900 mb-3">현재 입주자</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm text-blue-700 font-medium">입주자 정보</div>
                      <div className="mt-1">
                        <div className="font-medium">{room.currentResident.name}</div>
                        <div className="text-sm text-gray-600">{room.currentResident.phoneNumber}</div>
                        <div className="text-sm text-gray-600">{room.currentResident.organization}</div>
                      </div>
                    </div>
                    {room.occupancyPeriod && (
                                             <div>
                         <div className="text-sm text-blue-700 font-medium">입주 기간</div>
                         <div className="mt-1">
                           <div className="text-sm">
                             {formatDate(room.occupancyPeriod.startDate)} ~ {formatDate(room.occupancyPeriod.endDate)}
                           </div>
                         </div>
                       </div>
                    )}
                  </div>
                </div>
              )}

              {/* 과거 입주 히스토리 */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  과거 입주 기록 ({contractHistory.length}건)
                </h3>
                <div className="space-y-4">
                  {contractHistory.map((record, index) => (
                    <div key={record.id} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center space-x-2">
                          <span className="bg-gray-600 text-white text-xs px-2 py-1 rounded">
                            #{contractHistory.length - index}
                          </span>
                          <span className="text-sm text-gray-500">
                            계약일: {formatDateTime(record.contractSignedAt)}
                          </span>
                        </div>
                        {record.moveOutInspection?.signedAt && (
                          <button
                            onClick={() =>
                              setExpandedContractId(prev => (prev === record.id ? null : record.id))
                            }
                            className="text-sm font-medium text-red-700 hover:text-red-900"
                            title="퇴거점검 내역 보기"
                          >
                            퇴거점검 {expandedContractId === record.id ? '접기' : '보기'}
                          </button>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <div className="text-sm font-medium text-gray-700">입주자 정보</div>
                          <div className="mt-1">
                            <div className="font-medium">{record.contractorInfo.name}</div>
                            <div className="text-sm text-gray-600">{record.contractorInfo.phoneNumber}</div>
                            <div className="text-sm text-gray-600">{record.contractorInfo.organization}</div>
                          </div>
                        </div>
                        
                                                  <div>
                            <div className="text-sm font-medium text-gray-700">입주 기간</div>
                            <div className="mt-1">
                              <div className="text-sm">
                                {formatDate(record.occupancyPeriod.startDate)} ~ {formatDate(record.occupancyPeriod.endDate)}
                              </div>
                            </div>
                          </div>
                        
                        <div>
                          <div className="text-sm font-medium text-gray-700">계약 상태</div>
                          <div className="mt-1">
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                              계약 완료
                            </span>
                            {record.signatureUrl && (
                              <div className="mt-1">
                                <span className="text-xs text-gray-500">서명 완료</span>
                              </div>
                            )}
                            {record.moveOutInspection?.signedAt && (
                              <div className="mt-2">
                                <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                                  퇴거점검 완료 ({formatDate(record.moveOutInspection.signedAt)})
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {record.moveOutInspection?.signedAt && expandedContractId === record.id && (
                        <div className="mt-4 bg-white border border-red-200 rounded-lg p-4">
                          <div className="flex flex-wrap items-center justify-between gap-3">
                            <h4 className="text-base font-semibold text-red-900">퇴거점검 내역</h4>
                            <button type="button" onClick={() => downloadInspection(record)}
                              disabled={generatingInspectionId !== null}
                              className="px-3 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed">
                              {generatingInspectionId === record.id ? 'PDF 생성 중...' : 'PDF 다운로드'}
                            </button>
                            {record.moveOutInspection.signatureUrl && (
                              <a
                                href={record.moveOutInspection.signatureUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="text-sm text-red-700 hover:text-red-900 underline"
                              >
                                서명 보기
                              </a>
                            )}
                          </div>
                          {pdfError?.id === record.id && <p role="alert" className="mt-2 text-sm text-red-700">{pdfError.message}</p>}

                          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <div className="text-sm font-medium text-gray-700">점검일</div>
                              <div className="mt-1 text-sm text-gray-900">
                                {formatDateTime(record.moveOutInspection.signedAt)}
                              </div>
                            </div>
                            <div>
                              <div className="text-sm font-medium text-gray-700">점검자</div>
                              <div className="mt-1 text-sm text-gray-900 whitespace-pre-wrap break-words">
                                {record.moveOutInspection.inspectorName || '미기록'}
                              </div>
                            </div>
                            <div>
                              <div className="text-sm font-medium text-gray-700">검침수치</div>
                              <div className="mt-1 text-sm text-gray-900">
                                전기 {record.moveOutInspection.meterReadings?.electricity || '-'} / 수도 {record.moveOutInspection.meterReadings?.water || '-'} / 가스 {record.moveOutInspection.meterReadings?.gas || '-'}
                              </div>
                            </div>
                          </div>

                          {record.moveOutInspection.charges && (
                            <div className="mt-4">
                              <h5 className="font-semibold text-gray-900 mb-2">퇴거 납부금액 안내</h5>
                              <MoveOutChargesSummary charges={record.moveOutInspection.charges} recipient={record.contractorInfo} />
                            </div>
                          )}

                          {record.moveOutInspection.notes && (
                            <div className="mt-4">
                              <div className="text-sm font-medium text-gray-700">점검 메모</div>
                              <div className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">
                                {record.moveOutInspection.notes}
                              </div>
                            </div>
                          )}

                          {record.moveOutInspection.items && record.moveOutInspection.items.length > 0 && (
                            <div className="mt-4">
                              <div className="text-sm font-medium text-gray-700 mb-2">점검 항목</div>
                              <div className="space-y-3">
                                {groupedMoveOutItems(record.moveOutInspection.items).map(([category, items]) => (
                                  <div key={category} className="border border-gray-200 rounded-md p-3">
                                    <div className="text-sm font-semibold text-gray-900 mb-2">{category}</div>
                                    <div className="space-y-2">
                                      {items.map((item) => (
                                        <div key={item.id} className="flex items-start justify-between gap-3">
                                          <div className="text-sm text-gray-900">
                                            <span className="font-medium">{item.name || item.id}</span>
                                            {item.notes ? (
                                              <div className="text-xs text-gray-600 whitespace-pre-wrap">{item.notes}</div>
                                            ) : null}
                                          </div>
                                          <div className="text-xs">
                                            <span className={`inline-flex px-2 py-1 rounded-full font-semibold ${
                                              item.checked ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'
                                            }`}>
                                              {item.checked ? '확인' : '미확인'}
                                            </span>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* 추첨 기록 */}
              {lotteryHistory.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    추첨 기록 ({lotteryHistory.length}건)
                  </h3>
                  <div className="space-y-4">
                    {lotteryHistory.map((record, index) => (
                      <div key={record.id} className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center space-x-2">
                            <span className="bg-yellow-600 text-white text-xs px-2 py-1 rounded">
                              추첨 #{lotteryHistory.length - index}
                            </span>
                            <span className="text-sm text-gray-500">
                              추첨일: {formatDateTime(record.drawnAt)}
                            </span>
                            {record.contractedAt && (
                              <span className="text-sm text-gray-500">
                                계약일: {formatDateTime(record.contractedAt)}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <div className="text-sm font-medium text-gray-700">추첨자 정보</div>
                            <div className="mt-1">
                              <div className="font-medium">{record.userName || '정보 없음'}</div>
                              <div className="text-sm text-gray-600">{record.userPhone || '정보 없음'}</div>
                              <div className="text-sm text-gray-600">{record.userOrganization || '정보 없음'}</div>
                            </div>
                          </div>
                          
                          <div>
                            <div className="text-sm font-medium text-gray-700">호실 정보</div>
                            <div className="mt-1">
                              <div className="text-sm font-medium">{record.roomNumber}호</div>
                            </div>
                          </div>
                          
                          <div>
                            <div className="text-sm font-medium text-gray-700">상태</div>
                            <div className="mt-1">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                record.status === 'contracted' 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {record.status === 'contracted' ? '계약 완료' : '추첨 완료'}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* 계약 전 제출 서류 (추첨 후 업로드) */}
                        {record.documents && Object.keys(record.documents).length > 0 && (
                          <div className="mt-4 border-t border-yellow-200 pt-4">
                            <div className="text-sm font-medium text-gray-700 mb-2">계약 전 제출 서류</div>
                            <div className="flex flex-col gap-2">
                              {(['identification', 'employment', 'other'] as const).map((type) => {
                                const path = record.documents?.[type];
                                if (!path) return null;
                                const isDownloading = downloadingPath === path;
                                return (
                                  <div key={type} className="flex items-center justify-between gap-3 bg-white/70 border border-yellow-200 rounded px-3 py-2">
                                    <div className="text-sm text-gray-800">
                                      <span className="font-medium">{getDocumentDisplayName(type)}</span>
                                      <span className="ml-2 text-xs text-gray-500">{getOriginalFileNameFromStoragePath(path)}</span>
                                    </div>
                                    <button
                                      onClick={() =>
                                        downloadLotteryDocument(
                                          path,
                                          `${record.roomNumber}호_${record.userName || record.uid}_${getDocumentDisplayName(type)}`
                                        )
                                      }
                                      disabled={Boolean(downloadingPath)}
                                      className={`px-3 py-1.5 rounded text-sm ${
                                        Boolean(downloadingPath)
                                          ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                          : 'bg-blue-600 text-white hover:bg-blue-700'
                                      }`}
                                    >
                                      {isDownloading ? '다운로드 중...' : '다운로드'}
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                            {record.documentsUpdatedAt && (
                              <div className="mt-2 text-xs text-gray-500">
                                업로드 업데이트: {formatDateTime(record.documentsUpdatedAt)}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* 푸터 */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            닫기
          </button>
        </div>
      </div>
    </div>
  );
};

export default RoomHistoryModal; 
